import { useMemo, useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import Select from 'components/FormUtils/Select'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { TextInput } from 'components/FormUtils/TextInput'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { US_STATES } from 'utils/us_states'
import { US_CITIES } from 'utils/us_cities'
import apiService from 'services/api'
import { ContactResponse } from 'models/contacts'
import { ErrorLabel } from 'components/FormUtils/ErrorLabel'
import { cn } from 'components/UI/cn'

const ErrorText = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <div className={`text-red-600 text-xs mt-1 ${className}`}>{children}</div>

export function MobileContactShippingStep() {
  const api = apiService()
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const [useManualContact, setUseManualContact] = useState(false)

  const contactCompanyId = watch('contact_company_id')
  const watchState = watch('shipping_state')

  // Contacts for selected company
  const { data: contactsResponse, isLoading: isLoadingContacts } =
    api.useGetContactsList(
      contactCompanyId ? { company: contactCompanyId } : {},
      undefined,
      !!contactCompanyId
    )

  const contacts = contactsResponse?.results ?? []

  const contactOptions = useMemo(
    () =>
      contacts.map((c: ContactResponse) => ({
        label: `${c.first_name} ${c.last_name}${
          c.starred ? ' ⭐' : ''
        }${c.email_preferred || c.email_work ? ` - ${c.email_preferred || c.email_work}` : ''}`,
        value: c.id,
        raw: c,
      })),
    [contacts]
  )

  const stateOptions = US_STATES

  const cityOptions = useMemo(() => {
    const state = watchState
    if (!state) return []
    return US_CITIES.filter((city) => city.value.endsWith(`_${state}`)).map(
      (city) => ({
        label: city.label.split(',')[0],
        value: city.label.split(',')[0],
      })
    )
  }, [watchState])

  const handleSelectContact = (contact: ContactResponse | null) => {
    if (!contact) {
      setValue('contact_id', null, { shouldValidate: true })
      setValue('contact_first_name', '', { shouldValidate: true })
      setValue('contact_last_name', '', { shouldValidate: true })
      setValue('contact_email', '', { shouldValidate: true })
      setValue('contact_phone', '', { shouldValidate: true })
      return
    }

    setValue('contact_id', contact.id, { shouldValidate: true })
    setValue('contact_first_name', contact.first_name || '', {
      shouldValidate: true,
    })
    setValue('contact_last_name', contact.last_name || '', {
      shouldValidate: true,
    })
    setValue(
      'contact_email',
      contact.email_preferred ||
        contact.email_work ||
        contact.email_personal ||
        '',
      { shouldValidate: true }
    )
    setValue('contact_phone', contact.phone_number || '', {
      shouldValidate: true,
    })
  }

  const handleSwitchToManual = () => {
    setUseManualContact(true)
    // clear selected_contact_id so select shows placeholder
    setValue('selected_contact_id', null, { shouldValidate: false })
    // but keep whatever is in the manual fields (user may have typed already)
  }

  const handleSwitchToSelect = () => {
    setUseManualContact(false)
    // manual fields remain, but validation will enforce selection or manual values
  }

  return (
    <div className="flex flex-col gap-6 px-4 pb-4 pt-2">
      {/* Top title */}
      <div className="flex flex-col gap-1">
        <div className="text-[11px] font-semibold tracking-[0.12em] text-gray-500 uppercase">
          Create a Sample Request
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          Contact & Shipping Details
        </h2>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h2 className="mt-2 text-xs font-semibold text-gray-500 tracking-[0.06em]">
          Contact Information
        </h2>

        {/* Select Contact + Enter Manually toggle row */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900">
            Contact<span className="text-red-500">*</span>
          </span>
          <button
            type="button"
            onClick={
              useManualContact ? handleSwitchToSelect : handleSwitchToManual
            }
            className="text-xs font-medium text-fb-dark-green underline"
          >
            {useManualContact ? 'Select Existing' : 'Enter Manually'}
          </button>
        </div>

        {/* Select Contact mode */}
        {!useManualContact && (
          <div>
            <Controller
              name="selected_contact_id"
              control={control}
              render={({ field }) => (
                <>
                  <ReactSelectWrapper
                    {...field}
                    onChange={(newValue: any) => {
                      const id = newValue?.value ?? null
                      field.onChange(id)
                      const found = contactOptions.find(
                        (opt) => opt.value === id
                      )
                      handleSelectContact(found?.raw ?? null)
                    }}
                    options={contactOptions}
                    isLoading={isLoadingContacts}
                    placeholder={
                      contacts.length === 0
                        ? 'No contacts available – enter manually'
                        : 'Select a contact...'
                    }
                    value={
                      contactOptions.find((opt) => opt.value === field.value) ??
                      null
                    }
                    isClearable
                  />
                  {(errors.contact_first_name ||
                    errors.contact_last_name ||
                    errors.contact_email) && (
                    <ErrorText>
                      Please select a contact or enter contact details manually.
                    </ErrorText>
                  )}
                </>
              )}
            />
          </div>
        )}

        {/* Manual entry mode */}
        {useManualContact && (
          <>
            {/* First / Last name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-base font-medium text-gray-800 mb-1">
                  Contact Name *
                </label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                             placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
                  placeholder="First Name"
                  {...register('contact_first_name')}
                />
                {errors.contact_first_name && (
                  <ErrorText>
                    {String(errors.contact_first_name.message)}
                  </ErrorText>
                )}
              </div>
              <div className="pt-[24.5px]">
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                             placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
                  placeholder="Last Name"
                  {...register('contact_last_name')}
                />
                {errors.contact_last_name && (
                  <ErrorText>
                    {String(errors.contact_last_name.message)}
                  </ErrorText>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="text-base font-medium text-gray-800 mb-1">
                Contact Email *
              </label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                           placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
                placeholder="name@company.com"
                {...register('contact_email')}
              />
              {errors.contact_email && (
                <ErrorText>{String(errors.contact_email.message)}</ErrorText>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Use work email – should match the company domain.
              </p>
            </div>

            {/* Phone */}
            <div>
              <label className="text-base font-medium text-gray-800 mb-1">
                Contact Phone Number
                <span className="text-sm italic font-normal text-gray-400">
                  {' '}
                  - optional
                </span>
              </label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                           placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
                placeholder="US +1 650 888 8888"
                {...register('contact_phone')}
              />
              <p className="mt-1 text-xs text-gray-500">
                Use format: +1 ### ### ####
              </p>
              {errors.contact_phone && (
                <ErrorText>{String(errors.contact_phone.message)}</ErrorText>
              )}
            </div>
          </>
        )}
      </div>

      {/* Shipping Information */}
      <div className="space-y-4">
        <h2 className="mt-2 text-xs font-semibold text-gray-500 tracking-[0.06em]">
          Shipping Information
        </h2>

        <div>
          <label className="text-base font-medium text-gray-800 mb-1">
            Address Line 1 *
          </label>
          <input
            className={cn(
              'w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm',
              'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600',
              errors.shipping_address_line1 ? 'input-error' : ''
            )}
            placeholder="Street address"
            {...register('shipping_address_line1')}
          />
          {errors.shipping_address_line1 && (
            <ErrorLabel
              message={String(errors.shipping_address_line1.message)}
            />
          )}
        </div>

        <div>
          <label className="text-base font-medium text-gray-800 mb-1">
            Address Line 2
          </label>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                       placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
            placeholder="Apt, suite, unit, etc. (optional)"
            {...register('shipping_address_line2')}
          />
        </div>

        <div className="flex gap-2 w-full max-w-full mt-1">
          <Select
            className="flex-1 mt-0"
            control={control}
            name="shipping_state"
            placeholder="State"
            options={stateOptions}
            label="State *"
            optional={false}
          />
          <CreatableSelect
            className="flex-1 mt-0"
            control={control}
            name="shipping_city"
            placeholder="City"
            options={cityOptions}
            label="City *"
            optional={false}
            disabled={!watchState}
            onCreateOption={async (city) => ({ label: city, value: city })}
          />
        </div>

        <div className="flex gap-2 w-full max-w-full mt-1 mb-4">
          <TextInput
            label="Zip/Postal Code *"
            name="shipping_zip"
            control={control}
            placeholder="Zip/postal code"
            optional={false}
            maxLength={5}
            type="text"
          />
        </div>
      </div>
    </div>
  )
}
